import { createApiClient } from '@daily/contracts/api';
import { createBrowserAuthGateway } from '../session/auth-gateway.js';
import { createBrowserAuthClient } from '../session/auth-runtime.mjs';
import { fitPhoto } from '../uploads/fit-photo.js';
import { directUploadTransport } from '../uploads/transport.js';
import { purgeDeviceVoiceRecordings } from '../voice/privacy.js';
import { deviceTimezone } from './auth.js';
import type { AuthRuntime } from './auth.js';
import { createV1BackendWith } from './backend.js';
import type { V1Backend } from './backend.js';

/** Where this tab keeps the ids of its unfinished uploads. A private window or blocked site data has no such place: then nothing is kept. */
const UNFINISHED_UPLOADS = 'kalndar.uploads';
const unfinishedUploads = {
  read(): readonly string[] {
    try { const kept: unknown = JSON.parse(sessionStorage.getItem(UNFINISHED_UPLOADS) ?? '[]'); return Array.isArray(kept) ? kept.filter((id): id is string => typeof id === 'string') : []; }
    catch { return []; }
  },
  write(ids: readonly string[]): void {
    try { if (ids.length) sessionStorage.setItem(UNFINISHED_UPLOADS, JSON.stringify(ids)); else sessionStorage.removeItem(UNFINISHED_UPLOADS); }
    catch { /* nothing is kept */ }
  },
};

/**
 * The v1 UI's data source and sign-in on this page's backend, with the browser's own transport,
 * Better Auth's client, direct media uploads and the device voice store.
 * Asks the backend once whether Google sign-in is offered; without an answer it is left out.
 */
export async function createV1Backend(): Promise<V1Backend> {
  const api = createApiClient((input, init) => globalThis.fetch(input, init));
  let google = false;
  try { google = (await api.request('socialCapabilities', {})).google; } catch { /* email and password stay available */ }
  // Google starts through the gateway: it alone navigates, and only to Google's own sign-in address.
  const session = createBrowserAuthClient(), gateway = createBrowserAuthGateway({ runtime: session });
  const auth: AuthRuntime = {
    signIn: (input) => session.signIn(input),
    signUp: (input) => session.signUp(input),
    requestPasswordReset: (email) => session.requestPasswordReset(email),
    resetPassword: (input) => session.resetPassword(input),
    resendVerification: (email) => session.resendVerification(email),
    signOut: () => session.signOut(),
    signInWithGoogle: () => gateway.signInWithGoogle(),
  };
  return createV1BackendWith({
    api, auth, google, transport: directUploadTransport, timezone: deviceTimezone,
    key: () => crypto.randomUUID(), now: () => Date.now(), sleep: (ms) => new Promise((resolve) => { setTimeout(resolve, ms); }),
    preview: (file) => (typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file) : null),
    purgeVoice: purgeDeviceVoiceRecordings, unfinishedUploads, fit: fitPhoto,
  });
}
